import { memo, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { CallIntelConstant, CallIntelTypes } from '@frontend/api-call-intel';
import { DemoChip } from '@frontend/analytics-shared';
import {
  CallIntelTooltip,
  generateTrackingId,
  sortWithPriorityMap,
  useCallIntelDefaultsShallowStore,
} from '@frontend/call-intel-core';
import { Chart } from '@frontend/charts';
import { AnimatedBadge } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Heading, Text } from '@frontend/design-system';
import { ChartInsightCard } from '../chart-insight-card';
import { CustomTooltipTitle } from '../custom-tooltip-title';
import { useActiveFilters } from '../hooks/use-active-filters';
import { useCallIntelDemoFlags } from '../hooks/use-call-intel-demo-flags';
import { useCallIntelShallowStore } from '../hooks/use-call-intel-store';
import { useServiceIssueInsight } from '../hooks/use-service-issue-insight';

type ChartGroup = { name: string; values: Record<string, number> };
type ChartData = { groups: ChartGroup[]; values: Record<string, number> };

type ServiceIssueRouteSlug = keyof typeof CallIntelConstant.AllowedServiceIssuesRouteLabelMap;

type Props = {
  isLoading?: boolean;
  serviceQualityStats?: CallIntelTypes.ServiceQualityStats;
};

const BAR_APPEARANCE = {
  barSize: 40,
  groupsGap: 24,
  layout: 'vertical' as const,
  showYAxis: false,
  margin: { top: 0, right: 8, left: 16, bottom: 0 },
  hideTooltip: false,
};

const DEFAULT_ORDER = (
  Object.entries(CallIntelConstant.ServiceQualityFlagPriorityMap) as [CallIntelTypes.ServiceQualityFlagEnum, number][]
)
  .sort(([, prioA], [, prioB]) => prioA - prioB)
  .map(([flag]) => flag);

const FLAG_TO_SLUG: Record<CallIntelTypes.ServiceQualityFlagEnum, ServiceIssueRouteSlug> = (
  Object.entries(CallIntelConstant.AllowedServiceIssuesRouteLabelMap) as [
    ServiceIssueRouteSlug,
    CallIntelTypes.ServiceQualityFlagEnum
  ][]
).reduce((acc, [slug, flag]) => {
  acc[flag] = slug;
  return acc;
}, {} as Record<CallIntelTypes.ServiceQualityFlagEnum, ServiceIssueRouteSlug>);

const useChartData = (stats?: CallIntelTypes.ServiceQualityStats): ChartData => {
  return useMemo(() => {
    const rows = stats?.currentStats?.serviceQualityStats ?? [];

    // Accumulate total calls per issue
    const totalsByIssue = rows.reduce<Record<string, number>>((acc, { serviceQualityIssue, callsCount = 0 }) => {
      if (!serviceQualityIssue) return acc;
      const key = String(serviceQualityIssue);
      acc[key] = (acc[key] ?? 0) + callsCount;
      return acc;
    }, {});

    // sort by priority map
    const sorted = sortWithPriorityMap(Object.keys(totalsByIssue), CallIntelConstant.ServiceQualityFlagPriorityMap);

    // build groups + values
    const data: ChartData = { groups: [], values: {} };
    sorted.forEach((name) => {
      const count = totalsByIssue[name];
      data.groups.push({ name, values: { [name]: count } });
      data.values[name] = count;
    });
    return data;
  }, [stats]);
};

export const ServiceIssueChart = memo(({ isLoading, serviceQualityStats }: Props) => {
  const { t } = useTranslation('analytics');
  const navigate = useNavigate();
  const { showDemoChipAndBanner } = useCallIntelDemoFlags();
  const { chartOutlineColors, chartLabelColors, dataColors, dataLabels } = useCallIntelDefaultsShallowStore(
    'chartOutlineColors',
    'chartLabelColors',
    'dataColors',
    'dataLabels'
  );
  const { setSubView } = useCallIntelShallowStore('setSubView');
  const { filterHintText } = useActiveFilters();

  const colors = dataColors.serviceQualityFlags ?? {};
  const labelColors = chartLabelColors.serviceQualityFlags ?? {};
  const strokeColors = chartOutlineColors.serviceQualityFlags ?? {};
  const labels = dataLabels.serviceQualityLabels ?? {};

  const chartData = useChartData(serviceQualityStats);

  const barColors = useMemo(
    () =>
      DEFAULT_ORDER.reduce<Record<string, string>>((acc, flag) => {
        const count = chartData.values[flag] ?? 0;
        acc[flag] = count > 0 ? colors[flag] : theme.colors.neutral20;
        return acc;
      }, {}),
    [chartData.values, colors]
  );

  const insight = useServiceIssueInsight({ stats: serviceQualityStats });

  const { isCurrentPartial, isPreviousPartial } = insight.isPartial;
  const { currentAnalyzedCalls, currentServiceIssueCalls, previousServiceIssueCalls } = insight.counts;

  const isEmptyValue = useMemo(
    () =>
      currentAnalyzedCalls === 0 ||
      (currentServiceIssueCalls === 0 && isCurrentPartial && previousServiceIssueCalls === 0 && isPreviousPartial),
    [currentAnalyzedCalls, currentServiceIssueCalls, previousServiceIssueCalls, isCurrentPartial, isPreviousPartial]
  );

  const onBarClick = useCallback(
    ({ group }: { group: string }) => {
      setSubView({ id: group, type: 'service-quality' });
      const slug = FLAG_TO_SLUG[group as CallIntelTypes.ServiceQualityFlagEnum] ?? group;
      navigate({ to: '/analytics/call-intelligence/service-issue/$type', params: { type: slug } });
    },
    [navigate, setSubView]
  );

  return (
    <Chart
      bottomContentRenderer={
        <ChartInsightCard
          title={insight.title}
          subtitle={insight.subTitle}
          muted={insight.muted}
          trackingId={generateTrackingId({ component: 'service-issue-chart-insight-card', context: 'click' })}
        />
      }
      css={styles.chartStyles}
      colors={barColors}
      labels={labels}
      strokeColors={strokeColors}
      isLoading={isLoading}
      style={{ marginBottom: 0 }}
    >
      <Chart.HeaderV2
        title={
          <Chart.HeaderV2.Title
            infoTip={<CallIntelTooltip tip='serviceIssues' />}
            leftElement={
              showDemoChipAndBanner ? (
                <DemoChip />
              ) : (
                <AnimatedBadge label={t('Beta')} animated={false} bgColor={theme.colors.indigo50} />
              )
            }
          >
            {t('Service Issues')}
          </Chart.HeaderV2.Title>
        }
        subTitle={
          <Text color='subdued' size='medium'>
            {t('Click each bar to learn more.')}
          </Text>
        }
        action={<Chart.HeaderV2.FilterText filterHintText={filterHintText} />}
      />
      <div css={styles.contentStyles(isEmptyValue)}>
        <div css={styles.labelValueWrapper} data-testid='service-issues-stat'>
          {!isEmptyValue ? (
            DEFAULT_ORDER.map((flag) => (
              <div css={styles.labelValueRow} key={flag} data-testid={`service-issue-${flag}`}>
                <Heading level={2}>{chartData.values[flag]}</Heading>
                <div css={styles.labelWrapper}>
                  <Text size='medium'>{labels?.[flag] || flag}</Text>
                  <CallIntelTooltip tip={CallIntelConstant.ServiceQualityTooltipIdMap[flag]!} />
                </div>
              </div>
            ))
          ) : (
            <div css={styles.emptyText}>
              <Text color='subdued' css={styles.emptyText}>
                {t('No data available')}
              </Text>
            </div>
          )}
        </div>
        <Chart.BarChart
          appearance={{
            ...BAR_APPEARANCE,
            customTooltipTitle: ({ groupName }) => {
              return <CustomTooltipTitle label={labels?.[groupName] || groupName} />;
            },
            customTooltipData: (id) => [
              {
                color: labelColors[id],
                formattedValue: chartData.values[id],
                id,
                label: labels[id],
                subLabel: t('Out of {{count}} Calls Analyzed', {
                  count: serviceQualityStats?.currentStats?.analyzedCallsCount ?? 0,
                }),
              },
            ],
          }}
          data={{
            groups: chartData.groups,
          }}
          formatValue={formatters.value}
          onClick={onBarClick}
          isUsedInCategoryBarChart={isEmptyValue ? false : true}
          css={styles.barChart}
          baseTrackingId={generateTrackingId({ component: 'service-issues-bar-chart' })}
        />
      </div>
    </Chart>
  );
});

ServiceIssueChart.displayName = 'ServiceIssueChart';

const styles = {
  chartStyles: css`
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.neutral20};

    .chart-content {
      border: none;
      margin-bottom: 0;
      padding: ${theme.spacing(3)};
    }
  `,
  contentStyles: (isEmptyValue: boolean) => css`
    display: ${isEmptyValue ? 'flex' : 'grid'};
    ${isEmptyValue ? 'flex-direction: column-reverse;' : 'grid-template-columns: minmax(0, max-content) auto;'}
    gap: ${theme.spacing(isEmptyValue ? 2 : 0)};
  `,
  barChart: css`
    overflow: hidden;
  `,
  labelValueWrapper: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    justify-content: space-evenly;
  `,
  labelValueRow: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    min-height: ${theme.spacing(5)};
  `,
  labelWrapper: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
  `,
  emptyText: css`
    text-align: center;
  `,
  chartHeader: css`
    .title-wrapper {
      .heading {
        margin-left: ${theme.spacing(1)};
      }
    }
  `,
};
